import { useTranslation } from 'react-i18next';
import { formatDkkCompact } from '../../utils/currencyUtils';
import type { MetricView } from '../../services/overviewMetricsService';
import styles from './MoneyLegend.module.css';

interface MoneyLegendProps {
  view: MetricView;
  lockedFromWeek: number;
  lockedToWeek: number;
}

/**
 * The figures behind the track.
 *
 * Carries one number the bar deliberately does not draw: the shortfall, which is
 * locked potential minus what landed. Because realised is a caret rather than a
 * segment, that gap would otherwise be a subtraction the reader has to perform
 * per location, so it is stated as a derived row under the locked figure.
 *
 * The label is the <dt> and the figure its <dd>, which is the way round a
 * description list means: "still plannable" is the term, the amount describes
 * it. The figure still reads first on screen — see the ordering in the module
 * CSS — because the eye scans the numbers, not the words.
 */
export const MoneyLegend = ({ view, lockedFromWeek, lockedToWeek }: MoneyLegendProps) => {
  const { t, i18n } = useTranslation();
  const format = (value: number): string => formatDkkCompact(value, i18n.language);

  return (
    <dl className={styles.legend}>
      {view.lockedPotential > 0 && (
        <>
          <div className={styles.row}>
            <span className={`${styles.chip} ${styles.chipLocked}`} aria-hidden="true" />
            <dt className={styles.label}>
              {/* 0 is the unset sentinel, not an ISO week, so a payload with
                  locked money but no week range says "locked" without inventing
                  "weeks 0-0". */}
              {lockedFromWeek > 0 && lockedToWeek > 0
                ? t('overview-legend-locked', { fromWeek: lockedFromWeek, toWeek: lockedToWeek })
                : t('year-plan-kpi-locked')}
            </dt>
            <dd className={styles.value}>{format(view.lockedPotential)}</dd>
          </div>
          {view.shortfall > 0 && (
            <div className={`${styles.row} ${styles.derived}`}>
              <dt className={`${styles.label} ${styles.lost}`}>
                {t('overview-legend-did-not-land')}
              </dt>
              <dd className={styles.value}>{format(view.shortfall)}</dd>
            </div>
          )}
        </>
      )}
      {view.openPotential > 0 && (
        <div className={styles.row}>
          <span className={`${styles.chip} ${styles.chipOpen}`} aria-hidden="true" />
          <dt className={styles.label}>{t('overview-legend-plannable')}</dt>
          <dd className={styles.value}>{format(view.openPotential)}</dd>
        </div>
      )}
      {view.isOverTarget ? (
        <div className={styles.row}>
          <span className={`${styles.chip} ${styles.chipExcess}`} aria-hidden="true" />
          <dt className={`${styles.label} ${styles.lost}`}>{t('overview-legend-excess')}</dt>
          <dd className={styles.value}>{format(view.excess)}</dd>
        </div>
      ) : (
        <div className={styles.row}>
          <span className={`${styles.chip} ${styles.chipRoom}`} aria-hidden="true" />
          <dt className={styles.label}>{t('overview-legend-room')}</dt>
          <dd className={styles.value}>{format(view.roomToTarget)}</dd>
        </div>
      )}
    </dl>
  );
};
